import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Star, Send, Loader2, CheckCircle2, Shield, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { storage } from '@/lib/storage';

// Star Rating Component
const StarRating = ({ rating, onRate, readonly = false, size = 'md', label }) => {
  const [hover, setHover] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-sm font-medium">{label}</Label>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onRate && onRate(star)}
            onMouseEnter={() => !readonly && setHover(star)}
            onMouseLeave={() => !readonly && setHover(0)}
            className={`focus:outline-none transition-transform hover:scale-110 ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <Star
              className={`${sizeClasses[size]} ${
                star <= (hover || rating)
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export const AddReviewForm = ({ propertyId, propertyTitle, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Request code, 2: Enter code and review
  const [requestingCode, setRequestingCode] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    comment: '',
    stay_date: ''
  });

  const user = storage.getUser();
  const isAuthenticated = !!user;

  // Check if user already has a pending request
  useEffect(() => {
    const checkPendingRequest = async () => {
      if (!isAuthenticated || !propertyId) return;
      
      try {
        const response = await api.get('/reviews/my-requests');
        const pending = response.data.requests.find(
          r => r.property_id === propertyId && (r.status === 'pending' || r.status === 'approved')
        );
        
        if (pending) {
          if (pending.status === 'approved' && pending.has_code) {
            setStep(2);
            setCodeRequested(true);
          } else if (pending.status === 'pending') {
            setCodeRequested(true);
          }
        }
      } catch (error) {
        console.error('Error checking pending requests:', error);
      }
    };
    
    if (isOpen) {
      checkPendingRequest();
    }
  }, [isOpen, propertyId, isAuthenticated]);

  const handleRequestCode = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para solicitar uma avaliação');
      return;
    }

    setRequestingCode(true);
    try {
      await api.post('/reviews/code/request', {
        property_id: propertyId,
        message: `Gostaria de avaliar o imóvel "${propertyTitle || 'este imóvel'}"`
      });
      
      setCodeRequested(true);
      toast.success('Solicitação enviada! O proprietário receberá uma notificação e enviará o código de verificação por email.');
    } catch (error) {
      console.error('Error requesting code:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao solicitar código';
      toast.error(errorMsg);
    } finally {
      setRequestingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    if (formData.rating === 0) {
      toast.error('Selecione uma avaliação');
      return;
    }
    if (!formData.title.trim() || formData.title.length < 3) {
      toast.error('Digite um título para sua avaliação');
      return;
    }
    if (!formData.comment.trim() || formData.comment.length < 10) {
      toast.error('Digite um comentário com pelo menos 10 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews/submit', {
        property_id: propertyId,
        code: verificationCode,
        rating: formData.rating,
        title: formData.title,
        comment: formData.comment,
        stay_date: formData.stay_date || null
      });

      toast.success('Avaliação enviada com sucesso!');
      setIsOpen(false);
      
      // Reset form
      setVerificationCode('');
      setFormData({ rating: 0, title: '', comment: '', stay_date: '' });
      setStep(1);
      setCodeRequested(false);
      
      // Callback to refresh reviews list
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    // Don't reset codeRequested if user already requested
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Faça login para avaliar este imóvel</p>
          <Button 
            className="mt-4"
            onClick={() => window.location.href = '/?login=true'}
          >
            Fazer Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-dashed">
          <CardContent className="py-8 text-center">
            <Star className="w-12 h-12 mx-auto mb-4 text-[#00C2D1]" />
            <p className="font-medium text-[#08172F]">Avaliar este imóvel</p>
            <p className="text-sm text-muted-foreground mt-1">
              {codeRequested 
                ? 'Você já solicitou um código. Clique para continuar.'
                : 'Hospedou aqui? Compartilhe sua experiência!'
              }
            </p>
            <Button className="mt-4 bg-[#00C2D1] hover:bg-[#00A8B5]">
              <MessageSquare className="w-4 h-4 mr-2" />
              {codeRequested ? 'Continuar Avaliação' : 'Escrever Avaliação'}
            </Button>
          </CardContent>
        </Card>
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Avaliar Imóvel</DialogTitle>
          <DialogDescription>
            {step === 1 && !codeRequested
              ? "Solicite um código de verificação ao proprietário para confirmar que você se hospedou neste imóvel."
              : step === 1 && codeRequested
              ? "Aguarde o proprietário aprovar sua solicitação. Você receberá o código por email."
              : "Digite o código de 6 dígitos que você recebeu por email."
            }
          </DialogDescription>
        </DialogHeader>
        
        {step === 1 ? (
          <div className="py-6">
            {!codeRequested ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-[#00C2D1]" />
                </div>
                <h3 className="font-medium mb-2">Verificação de Hóspede</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Para garantir avaliações autênticas, apenas hóspedes verificados podem avaliar.
                  Solicite seu código ao proprietário.
                </p>
                <Button 
                  onClick={handleRequestCode}
                  disabled={requestingCode}
                  className="w-full bg-[#00C2D1] hover:bg-[#00A8B5]"
                >
                  {requestingCode ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Solicitando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Solicitar Código de Verificação
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-medium mb-2">Solicitação Enviada!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  O proprietário foi notificado. Quando ele aprovar, você receberá o código por email.
                </p>
                <Badge className="bg-amber-100 text-amber-800">Aguardando aprovação</Badge>
                
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-muted-foreground mb-3">Já recebeu o código?</p>
                  <Button 
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="w-full"
                  >
                    Tenho o código, continuar
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Verification Code */}
            <div>
              <Label>Código de Verificação</Label>
              <Input
                placeholder="Digite o código de 6 dígitos"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono mt-1"
                data-testid="review-code-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                O código foi enviado para seu email
              </p>
            </div>
            
            {/* Rating */}
            <div className="flex justify-center py-2">
              <StarRating 
                rating={formData.rating} 
                onRate={(val) => setFormData({ ...formData, rating: val })}
                size="lg"
                label="Sua Avaliação"
              />
            </div>
            
            {/* Title */}
            <div>
              <Label>Título</Label>
              <Input
                placeholder="Resuma sua experiência em uma frase"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={100}
                className="mt-1"
                data-testid="review-title-input"
              />
            </div>
            
            {/* Comment */}
            <div>
              <Label>Comentário</Label>
              <Textarea
                placeholder="Conte mais sobre sua experiência..."
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                maxLength={1000}
                rows={4}
                className="mt-1"
                data-testid="review-comment-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.comment.length}/1000 caracteres
              </p>
            </div>
            
            {/* Stay Date */}
            <div>
              <Label>Quando você se hospedou? (opcional)</Label>
              <Input
                type="month"
                value={formData.stay_date}
                onChange={(e) => setFormData({ ...formData, stay_date: e.target.value })}
                className="mt-1"
                data-testid="review-stay-date-input"
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setStep(1)}
              >
                Voltar
              </Button>
              <Button 
                type="submit"
                disabled={submitting || !verificationCode || formData.rating === 0}
                className="bg-[#00C2D1] hover:bg-[#00A8B5]"
                data-testid="submit-review-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Publicar Avaliação'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddReviewForm;
